
import * as Cesium from "cesium";
import CommandCenterService from "../../../../service/CommandCenterService";
import { DEFAULT_ORBIT_COLOR } from "../../../../utils/constants";
import { getCartographicDegrees, getDataSourceByName, getEntityById, requestRender, showHideDataSourceByName } from "../../../../utils/cesiumHelperFunctions";
import { createMultiGroundObjectPacket, getEntitysByType, removeDataSourceByName } from "../../../../utils/cesiumUtil";
import { offline, truncateName } from "../../../../utils/commonFunctions";
import { GeoFeatureOptionType, GroundStationOptionType, TaskDetail } from "./scenario-types";
import MissionService from "../../../../service/MissionService";
import * as CesiumSensorVolumes from "../../../../utils/cesiumSensorsDist/cesium-sensors-es6.esm";
import { calculateOrbitPeriodSeconds } from "../../../CommonComponents/OrbitPeriods";
import { generateOrbitalTrackFromElements } from "../../../CommonComponents/cesium_helper/CesiumHelper";

// ── Red triangle billboard image (missile marker — matches GenericSimulator) ──
let _missileTriangleCanvas: HTMLCanvasElement | null = null;
const getMissileTriangleImage = (): HTMLCanvasElement => {
    if (_missileTriangleCanvas) return _missileTriangleCanvas;
    const size = 32;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    ctx.beginPath();
    ctx.moveTo(size / 2, 2);
    ctx.lineTo(size - 2, size - 2);
    ctx.lineTo(2, size - 2);
    ctx.closePath();
    ctx.fillStyle = '#FF4444';
    ctx.fill();
    ctx.strokeStyle = '#FF8888';
    ctx.lineWidth = 1;
    ctx.stroke();
    _missileTriangleCanvas = canvas;
    return canvas;
};

type SatelliteData = {
    id: string;
    lat: number;
    lon: number;
    alt: number;
    heading: number;
    pitch: number;
    roll: number;
};

let listeners: ((data: SatelliteData) => void)[] = [];
let predictionCleanupRefs: any[] = [];
let predictionVisibilityRefs: ((show: boolean) => void)[] = [];
let circularSensorCleanup: (() => void) | null = null;
let rectangularSensorCleanup: (() => void) | null = null;
let fitToScreenTimer: number | undefined;
let fitToScreenRequestId = 0;
const NON_PREDICTION_ORBIT_DATASOURCES = [
    "ORBIT_OBJECT",
    "__scenario_orbit_preview__",
    "__orbit_preview__"
];

const waitForClockSettle = () => new Promise<void>((resolve) => window.setTimeout(resolve, 0));

const applyPredictionClock = (
    viewer: Cesium.Viewer,
    predictionAvailability: string | undefined,
    predictionStartTime: string | undefined,
    multiplier: number,
    dataSource?: Cesium.DataSource
) => {
    if (!viewer || viewer.isDestroyed()) return;

    let startTime: Cesium.JulianDate | undefined;
    let stopTime: Cesium.JulianDate | undefined;

    try {
        if (predictionStartTime) {
            startTime = Cesium.JulianDate.fromIso8601(predictionStartTime);
        }

        const predictionStopTime = typeof predictionAvailability === 'string' ? predictionAvailability.split('/')[1] : undefined;
        if (predictionStopTime) {
            stopTime = Cesium.JulianDate.fromIso8601(predictionStopTime);
        }
    } catch (error) {
        console.error('Error parsing prediction clock interval:', error);
    }

    const dataSourceClock = (dataSource as any)?.clock;
    if (dataSourceClock) {
        if (startTime) dataSourceClock.currentTime = Cesium.JulianDate.clone(startTime);
        if (startTime) dataSourceClock.startTime = Cesium.JulianDate.clone(startTime);
        if (stopTime) dataSourceClock.stopTime = Cesium.JulianDate.clone(stopTime);
        dataSourceClock.multiplier = multiplier;
        dataSourceClock.clockRange = Cesium.ClockRange.CLAMPED;
        dataSourceClock.clockStep = Cesium.ClockStep.SYSTEM_CLOCK_MULTIPLIER;
        viewer.clockTrackedDataSource = dataSource as Cesium.DataSource;
    }

    if (startTime) viewer.clock.currentTime = Cesium.JulianDate.clone(startTime);
    if (startTime) viewer.clock.startTime = Cesium.JulianDate.clone(startTime);
    if (stopTime) viewer.clock.stopTime = Cesium.JulianDate.clone(stopTime);
    viewer.clock.clockRange = Cesium.ClockRange.CLAMPED;
    viewer.clock.clockStep = Cesium.ClockStep.SYSTEM_CLOCK_MULTIPLIER;
    viewer.clock.shouldAnimate = true;
    viewer.clock.multiplier = multiplier;
    requestRender(viewer);
};

export const getDefaultModelUrl = (busSizeInUnits: number): string => {
    if (busSizeInUnits <= 6) return '/assets/3U.glb';
    if (busSizeInUnits < 27) return '/assets/16U.glb';
    return '/assets/spike.glb';
};

export interface ModelScale {
    scale: number;
    minimumPixelSize: number;
}

export const getModelScale = (busSizeInUnits: number): ModelScale => {
    if (busSizeInUnits < 27) {
        return { scale: 1, minimumPixelSize: 100 };
    }
    return { scale: 0.5, minimumPixelSize: 50 };
};


export const getDefaultNadirOrbitAxis = (busSize: number) => {
    const nadirAxis = (busSize > 6 && busSize < 27) ? 'Z' : 'X'
    const orbitAxis = (busSize > 6 && busSize < 27) ? 'X' : 'Y'
    return { nadirAxis, orbitAxis }
}

export function onSatelliteUpdate(callback: (data: SatelliteData) => void) {
    listeners.push(callback);
    return () => {
        listeners = listeners.filter((cb) => cb !== callback);
    };
}

export function emitSatelliteUpdate(data: SatelliteData) {
    listeners.forEach((cb) => cb(data));
}

const fetchOrbitData = async (scenarioId: string, missionId: string) => {
    try {
        const response = await CommandCenterService.getnewOrbitData(missionId, scenarioId, "scenario")
        const data = response?.data
        return data
    } catch (error) {
        return null
    }
};

export const getSatelliteModelUrl = async (
    groupId: any,
    mission_id: any
) => {
    try {
        const res = await MissionService.get_satellite_by_sat_id(groupId, mission_id);
        if (!res?.data?.modelNames) return null;
        const nadirAxis = res.data?.modelNames?.nadirAxis;
        const orbitAxis = res.data?.modelNames?.orbitAxis;
        const modelRes = await MissionService.getModelUrl(
            groupId,
            res.data?.modelNames?.modelName
        );
        if (!modelRes?.data) return null;
        return {
            modelUrl: modelRes.data,
            nadirAxis,
            orbitAxis
        };
    } catch (error) {
        console.error("Error fetching satellite model info:", error);
        return null;
    }
};

const getGroundStationDetailsById = (groundStations: any[], connection: any): any | undefined => {
    if (!groundStations || !connection?.groundStationId) {
        return undefined;
    }
    const gs = groundStations.find(gs => gs?.gsId === connection.groundStationId);
    if (gs) gs.availability = connection?.availability;
    return gs;
};

export const createGroundPacket = (viewer: Cesium.Viewer | null, allGroundStations: GroundStationOptionType[], selectedStations: string[], selectedTaskplans: TaskDetail[], selectedFaultPlans: GeoFeatureOptionType[]) => {
    if (!viewer) return
    const { stations, taskplans, faultplans, multientity } = getEntitysByType(allGroundStations, selectedTaskplans, selectedFaultPlans)
    removeDataSourceByName(viewer, "FAULT_AOI_OBJECT");
    removeDataSourceByName(viewer, "FAULT_GT_OBJECT");
    removeDataSourceByName(viewer, "TASKPLAN_AOI_OBJECT");
    removeDataSourceByName(viewer, "TASKPLAN_GT_OBJECT");
    removeDataSourceByName(viewer, "GS_OBJECT");
    removeDataSourceByName(viewer, "MULTI_GROUND_OBJECT");
    createGsPackets(viewer, stations, selectedStations)
    createTaskPlanPacket(viewer, taskplans)
    createFaultPlanPacket(viewer, faultplans)
    createMultiGroundObjectPacket(viewer, multientity)
}

export const createSatellitePacket = async (
    viewer: Cesium.Viewer | null,
    scenarioId: any,
    missionId: any,
    selectedSatellite: any
) => {
    if (!viewer) return;
    const satellites = await fetchOrbitData(scenarioId, missionId);
    const satGroup = satellites?.satellites
    if (satGroup) {
        let satelliteData: any[] = [];
        satGroup.forEach(sat => {
            sat?.satelliteGroups?.flatMap(singleSat => {
                const satelliteId = singleSat?.satelliteId
                const isSelectedSatellite = satelliteId === selectedSatellite
                singleSat?.visualizationData?.satellites?.map(satellite => {
                    const rgbaArray = (satellite?.color !== '' && !satellite?.color.includes('#')) ? JSON.parse(satellite?.color) : DEFAULT_ORBIT_COLOR;
                    rgbaArray[3] = 150;
                    const packet = {
                        availability: satellite?.availability,
                        billboard: {
                            eyeOffset: { cartesian: [0, 0, 0] },
                            horizontalOrigin: "CENTER",
                            image: '/assets/satellite_default.svg',
                            pixelOffset: { cartesian2: [0, 0] },
                            scale: 0.75,
                            show: true,
                            verticalOrigin: "CENTER"
                        },
                        description: "orbit",
                        id: satelliteId,
                        path: {
                            leadTime: [{ number: calculateOrbitPeriodSeconds(satellite?.satOrbitParameters?.altitude) }],
                            width: 1,
                            material: {
                                solidColor: {
                                    color: {
                                        rgba: isSelectedSatellite
                                            ? [204, 245, 78, 255] : rgbaArray
                                    }
                                }
                            },
                            resolution: 10,
                            show: [{ boolean: true, interval: satellite?.availability }],
                            trailTime: [{ number: 5000 }]
                        },
                        position: {
                            cartographicDegrees: satellite?.satOrbitPoint,
                            epoch: satellite?.availability?.split('/')[0]
                        }
                    };

                    satelliteData.push(packet);
                });
            });
        });

        showHideDataSourceByName(viewer, "PREDICTION_ORBIT", false);
        removeDataSourceByName(viewer, "ORBIT_OBJECT");
        // Remove the preview orbit when the permanent orbit is created
        removeDataSourceByName(viewer, "__scenario_orbit_preview__");
        const dataSource = new Cesium.CzmlDataSource();
        viewer.dataSources.add(dataSource.load([
            {
                clock: {
                    interval: satellites?.documentMetadata?.clock?.interval,
                    // Keep Cesium inside the orbit availability window so loaded orbit data renders immediately.
                    currentTime: satellites?.documentMetadata?.clock?.interval?.split('/')[0],
                    multiplier: 0,
                    range: "CLAMPED",
                    step: "SYSTEM_CLOCK_MULTIPLIER"
                },
                id: "document",
                name: "ORBIT_OBJECT",
                version: "1.0"
            },
            ...satelliteData
        ]));

        requestRender(viewer);
    }
};

export const createGsPackets = (viewer: Cesium.Viewer | null, ground_stations: any, selected_ground_stations: any) => {
    if (!viewer || !ground_stations?.length) return

    let gsPacketList: any[] = [];
    try {
        ground_stations.forEach((station: any) => {
            const gsId = station?.uniqueId
            const packet = {
                "billboard": {
                    "horizontalOrigin": Cesium.HorizontalOrigin.CENTER,
                    "verticalOrigin": Cesium.VerticalOrigin.CENTER,
                    "image": selected_ground_stations.includes(gsId)
                        ? '/assets/select_gs.svg'
                        : '/assets/un_select_gs.svg',
                    "scale": 0.8,
                    "show": true
                },
                "description": "station",
                "label": {
                    "fillColor": Cesium.Color.fromBytes(255, 255, 255, 255),
                    "font": "montserrat",
                    "horizontalOrigin": "LEFT",
                    "outlineColor": Cesium.Color.fromBytes(0, 0, 0, 255),
                    "outlineWidth": 2,
                    "pixelOffset": {
                        "cartesian2": [
                            15,
                            0
                        ]
                    },
                    "show": false,
                    "style": "FILL_AND_OUTLINE",
                    "verticalOrigin": "CENTER",
                    "text": station?.name,
                },
                "name": station?.name,
                "position": {
                    "cartographicDegrees": [
                        station?.long,
                        station?.lat,
                        0,
                    ]
                },
                "id": gsId,

            };
            gsPacketList.push(packet)
        })
        removeDataSourceByName(viewer, "GS_OBJECT")
        const dataSource = new Cesium.CzmlDataSource();
        viewer && viewer.dataSources.add(dataSource.load([
            {
                "id": "document",
                "name": "GS_OBJECT",
                "version": "1.0"
            },
            ...gsPacketList
        ]))
        requestRender(viewer);
    } catch (error) {
        console.error("Error creating GS packets:", error);
    }
}

export const createTaskPlanPacket = (viewer: Cesium.Viewer | null, taskplans: any[]) => {
    if (!viewer || !taskplans?.length) return
    const aois = taskplans.filter(f => f.type !== "Target Track");
    const groundTargets = taskplans.filter(f => f.type === "Target Track");
    let aoiData: any[] = [];
    let groundTargetData: any[] = [];
    try {
        aois && aois?.forEach((aoi: any, index) => {
            if (!aoi?.polygon) return
            let aoiPacket = {
                "description": "aoi",
                "id": aoi?.uniqueId,
                "name": aoi?.name,
                "polygon": {
                    "extrudedHeight": 0,
                    "height": 0,
                    "material": {
                        "solidColor": {
                            "color": {
                                "rgba": offline() ? [191, 191, 191, 100] : [204, 245, 78, 20]
                            }
                        }
                    },
                    "outline": true,
                    "outlineColor": {
                        "rgba": [204, 244, 94, 255]
                    },
                    "outlineWidth": 2,
                    "arcType": "RHUMB",
                    "positions": {
                        "cartographicDegrees": getCartographicDegrees(aoi?.polygon)
                    }
                }
            }
            aoiData.push(aoiPacket)
        })
        groundTargets && groundTargets?.forEach((groundTarget: any, index) => {
            let gtPacket = {
                "billboard": {
                    "eyeOffset": {
                        "cartesian": [
                            0,
                            0,
                            0
                        ]
                    },
                    "horizontalOrigin": "CENTER",
                    "image": '/assets/point_green.png',
                    "pixelOffset": {
                        "cartesian2": [
                            0,
                            0
                        ]
                    },
                    "scale": 1,
                    "show": true,
                    "verticalOrigin": "CENTER"
                },
                "description": "target",
                "id": groundTarget?.uniqueId,
                "label": {
                    "fillColor": {
                        "rgba": [
                            255,
                            255,
                            255,
                            255
                        ]
                    },
                    "font": "montserrat",
                    "horizontalOrigin": "LEFT",
                    "outlineColor": {
                        "rgba": [
                            0,
                            0,
                            0,
                            255
                        ]
                    },
                    "outlineWidth": 2,
                    "pixelOffset": {
                        "cartesian2": [
                            12,
                            0
                        ]
                    },
                    "show": true,
                    "style": "FILL_AND_OUTLINE",
                    "verticalOrigin": "CENTER"
                },
                "name": groundTarget?.['name'],
                "position": {
                    "cartographicDegrees": [
                        groundTarget?.long,
                        groundTarget?.lat,
                        0
                    ]
                }
            }
            groundTargetData.push(gtPacket)
        })

        removeDataSourceByName(viewer, "TASKPLAN_AOI_OBJECT");
        removeDataSourceByName(viewer, "TASKPLAN_GT_OBJECT");
        const aoi_dataSource = new Cesium.CzmlDataSource();
        const gt_dataSource = new Cesium.CzmlDataSource();

        if (aoiData.length) {
            viewer.dataSources.add(aoi_dataSource.load([
                {
                    "id": "document",
                    "name": "TASKPLAN_AOI_OBJECT",
                    "version": "1.0"
                },
                ...aoiData
            ]))
        }
        if (groundTargetData.length) {
            viewer.dataSources.add(gt_dataSource.load([
                {
                    "id": "document",
                    "name": "TASKPLAN_GT_OBJECT",
                    "version": "1.0"
                },
                ...groundTargetData
            ]))
        }
        requestRender(viewer);
    } catch (error) {
        console.log(error);
    }
}

export const createFaultPlanPacket = (viewer: Cesium.Viewer | null, faultplans: any[]) => {
    if (!viewer) return
    let aoiData: any[] = [];
    let groundTargetData: any[] = [];
    const aois = faultplans.filter(f => f.type !== "Target Track");
    const groundTargets = faultplans.filter(f => f.type === "Target Track");

    try {
        aois && aois?.forEach((aoi: any, index) => {
            const details = aoi
            if (!details?.polygon) return
            let aoiPacket = {
                "description": "aoi",
                "id": 'aoi_' + index,
                "name": details?.name,
                "polygon": {
                    "extrudedHeight": 0,
                    "height": 0,
                    "material": {
                        "solidColor": {
                            "color": {
                                "rgba": offline() ? [191, 191, 191, 100] : [242, 169, 50, 100]
                            }
                        }
                    },
                    "arcType": "RHUMB",
                    "positions": {
                        "cartographicDegrees": getCartographicDegrees(details?.polygon)
                    }
                }
            }
            aoiData.push(aoiPacket)
        })
        groundTargets && groundTargets?.forEach((groundTarget: any, index) => {
            const details = groundTarget

            let gtPacket = {
                "billboard": {
                    "eyeOffset": {
                        "cartesian": [
                            0,
                            0,
                            0
                        ]
                    },
                    "horizontalOrigin": "CENTER",
                    "image": '/assets/gs_target.svg',
                    "pixelOffset": {
                        "cartesian2": [
                            0,
                            0
                        ]
                    },
                    "scale": 1,
                    "show": true,
                    "verticalOrigin": "CENTER"
                },
                "description": "target",
                "id": 'GT_' + index,
                "label": {
                    "fillColor": {
                        "rgba": [
                            255,
                            255,
                            255,
                            255
                        ]
                    },
                    "font": "montserrat",
                    "horizontalOrigin": "LEFT",
                    "outlineColor": {
                        "rgba": [
                            0,
                            0,
                            0,
                            255
                        ]
                    },
                    "outlineWidth": 2,
                    "pixelOffset": {
                        "cartesian2": [
                            12,
                            0
                        ]
                    },
                    "show": true,
                    "style": "FILL_AND_OUTLINE",
                    "verticalOrigin": "CENTER"
                },
                "name": details?.['name'],
                "position": {
                    "cartographicDegrees": [
                        details?.long,
                        details?.lat,
                        0
                    ]
                }
            }
            groundTargetData.push(gtPacket)
        })

        removeDataSourceByName(viewer, "FAULT_AOI_OBJECT");
        removeDataSourceByName(viewer, "FAULT_GT_OBJECT");
        const aoi_dataSource = new Cesium.CzmlDataSource();
        const gt_dataSource = new Cesium.CzmlDataSource();

        if (aoiData.length) {
            viewer.dataSources.add(aoi_dataSource.load([
                {
                    "id": "document",
                    "name": "FAULT_AOI_OBJECT",
                    "version": "1.0"
                },
                ...aoiData
            ]))
        }
        if (groundTargetData.length) {
            viewer.dataSources.add(gt_dataSource.load([
                {
                    "id": "document",
                    "name": "FAULT_GT_OBJECT",
                    "version": "1.0"
                },
                ...groundTargetData
            ]))
        }
    } catch (error) {
        console.log(error);
    }
    requestRender(viewer);
}


export const selectUnselectGs = (viewer: Cesium.Viewer | null, ground_station: string, state: string) => {
    if (!viewer) return
    const entity: any = getEntityById(viewer, "GS_OBJECT", ground_station)
    if (entity) {
        if (state === 'checked') {
            entity.billboard.image = '/assets/select_gs.svg';
        } else {
            entity.billboard.image = '/assets/un_select_gs.svg';
        }
        requestRender(viewer);
    }
}

export const selectUnselectAllGs = (viewer: Cesium.Viewer | null, checked: boolean) => {
    if (!viewer) return
    const datasource: any = getDataSourceByName(viewer, "GS_OBJECT")
    if (datasource && datasource.entities && datasource.entities.values) {
        for (const entity of datasource.entities.values) {
            if (entity && entity.billboard) {
                entity.billboard.image = checked ? '/assets/select_gs.svg' : '/assets/un_select_gs.svg';
            }
        }
    }
    requestRender(viewer);
}


export const createGroundStationAvailabilityPacket = (groundStationsList, satelliteAltitudeMeters?: number) => {
    if (!groundStationsList) return [];
    let gsPacket: any[] = []

    const forHeight = typeof satelliteAltitudeMeters === 'number' && satelliteAltitudeMeters > 0
        ? satelliteAltitudeMeters
        : 500000 // fallback: 500km in meters (typical LEO altitude)
    const minElevation = 15 // degrees
    const forHalfAngle = Cesium.Math.toRadians(90 - minElevation) // 80 degrees from vertical
    const forTopRadius = forHeight * Math.tan(forHalfAngle)

    groundStationsList.forEach(gs => {
        const { gsId, gsName, gsPosition, availability } = gs;
        if (!availability || gsPosition?.length === 0) return;
        const availabilityArray = Array.isArray(availability) ? availability : [availability];
        gsPacket.push({
            id: `pred${gsId}`,
            name: `${gsName}`,
            availability: availabilityArray,
            position: {
                cartographicDegrees: [
                    gsPosition[0],
                    gsPosition[1],
                    0,
                ]
            },
            cylinder: {
                length: forHeight,
                topRadius: forTopRadius,
                bottomRadius: 0,
                material: {
                    solidColor: {
                        color: {
                            // RGBA bytes: cyan with ~0.08 alpha
                            rgba: [0, 255, 255, 20]
                        }
                    }
                },
                outline: true,
                outlineColor: {
                    // RGBA bytes: cyan with ~0.3 alpha
                    rgba: [0, 255, 255, 76]
                },
                numberOfVerticalLines: 32
            }
        });
    });
    return gsPacket
};

export const groundStationToSatelliteConnection = (satToGSConnection: any[]) => {
    const gsToSatConnection: any[] = [];

    satToGSConnection?.forEach(gsToSat => {
        if (!gsToSat?.availability?.length) return

        const interval: any[] = [];
        gsToSat?.availability?.forEach((item, key) => {
            const intervalPacket = {
                boolean: true,
                interval: item
            }
            interval?.push(intervalPacket)
        })

        const gsToSatPacket = {
            availability: gsToSat?.availability,
            description: "Ground Station to Satellite Connection",
            id: "pred" + gsToSat?.id,
            name: "pred" + gsToSat?.name,
            parent: "pred" + gsToSat?.satelliteId,
            polyline: {
                arcType: "NONE",
                material: {
                    solidColor: {
                        color: {
                            // Fully opaque for crisp, sharp line
                            rgba: [0, 250, 0, 255]
                        }
                    }
                },
                positions: {
                    references: [
                        "pred" + gsToSat?.groundStationId + "#position",
                        "pred" + gsToSat?.satelliteId + "#position"
                    ]
                },
                show: interval,
                width: 1
            }
        }
        gsToSatConnection.push(gsToSatPacket)
    })

    return gsToSatConnection
}

function getLocalAxis(axis) {
    //Coordinate system to match 3js canvas coorinate system
    const axisMap = {
        X: new Cesium.Cartesian3(0, 1, 0),
        "-X": new Cesium.Cartesian3(0, -1, 0),

        Y: new Cesium.Cartesian3(-1, 0, 0),
        "-Y": new Cesium.Cartesian3(1, 0, 0),

        Z: new Cesium.Cartesian3(0, 0, 1),
        "-Z": new Cesium.Cartesian3(0, 0, -1)
    };

    if (!axisMap[axis]) {
        throw new Error("Invalid axis: " + axis);
    }
    return axisMap[axis];
}

function quaternionFromTo(from, to) {
    const vFrom = Cesium.Cartesian3.normalize(from, new Cesium.Cartesian3());
    const vTo = Cesium.Cartesian3.normalize(to, new Cesium.Cartesian3());

    const dot = Cesium.Cartesian3.dot(vFrom, vTo);

    // Same direction
    if (dot > 0.999999) {
        return Cesium.Quaternion.IDENTITY;
    }

    // Opposite direction
    if (dot < -0.999999) {
        const ortho = Cesium.Cartesian3.cross(
            vFrom,
            Cesium.Cartesian3.UNIT_X,
            new Cesium.Cartesian3()
        );
        if (Cesium.Cartesian3.magnitudeSquared(ortho) < 1e-6) {
            Cesium.Cartesian3.cross(vFrom, Cesium.Cartesian3.UNIT_Y, ortho);
        }
        Cesium.Cartesian3.normalize(ortho, ortho);
        return Cesium.Quaternion.fromAxisAngle(ortho, Math.PI);
    }

    const axis = Cesium.Cartesian3.cross(vFrom, vTo, new Cesium.Cartesian3());
    const angle = Math.acos(dot);

    Cesium.Cartesian3.normalize(axis, axis);
    return Cesium.Quaternion.fromAxisAngle(axis, angle);
}


function getInitialAxisOffsetQuaternion(nadirAxis, orbitAxis) {

    // orbit cannot be same axis as nadir
    if (orbitAxis && nadirAxis.replace("-", "") === orbitAxis.replace("-", "")) {
        console.warn("Orbit axis cannot be collinear with nadir axis");
        orbitAxis = null;
    }

    // Step 1: align nadir axis → world -Z
    const localNadir = Cesium.Cartesian3.negate(
        getLocalAxis(nadirAxis),
        new Cesium.Cartesian3()
    );

    const worldDown = Cesium.Cartesian3.negate(
        Cesium.Cartesian3.UNIT_Z,
        new Cesium.Cartesian3()
    );

    const qNadir = quaternionFromTo(localNadir, worldDown);

    if (!orbitAxis) {
        return qNadir;
    }

    // Step 2: compute current world direction of orbit axis
    const localOrbit = getLocalAxis(orbitAxis);

    const rotMat = Cesium.Matrix3.fromQuaternion(qNadir);
    const worldOrbitCurrent = Cesium.Matrix3.multiplyByVector(
        rotMat,
        localOrbit,
        new Cesium.Cartesian3()
    );

    // Desired orbit direction (fixed reference)
    const worldOrbitDesired = Cesium.Cartesian3.UNIT_X;

    // Remove nadir component (safety)
    const proj = Cesium.Cartesian3.multiplyByScalar(
        worldDown,
        Cesium.Cartesian3.dot(worldOrbitCurrent, worldDown),
        new Cesium.Cartesian3()
    );
    Cesium.Cartesian3.subtract(worldOrbitCurrent, proj, worldOrbitCurrent);
    Cesium.Cartesian3.normalize(worldOrbitCurrent, worldOrbitCurrent);

    // Step 3: rotate around nadir to align orbit axis
    const qOrbit = quaternionFromTo(
        worldOrbitCurrent,
        worldOrbitDesired
    );

    // Step 4: combine
    return Cesium.Quaternion.multiply(
        qOrbit,
        qNadir,
        new Cesium.Quaternion()
    );
}

function applyInitialAxisOffset(quaternionData, nadirAxis, orbitAxis) {
    const qOffset = getInitialAxisOffsetQuaternion(nadirAxis, orbitAxis);
    const result: number[] = [];
    let prevQ: Cesium.Quaternion | null = null;
    for (let i = 0; i < quaternionData.length; i += 5) {
        const t = quaternionData[i];

        const qBackend = new Cesium.Quaternion(
            quaternionData[i + 1],
            quaternionData[i + 2],
            quaternionData[i + 3],
            quaternionData[i + 4]
        );

        // Apply ONE-TIME offset
        const qFinal = Cesium.Quaternion.multiply(
            qBackend,
            qOffset,
            new Cesium.Quaternion()
        );

        Cesium.Quaternion.normalize(qFinal, qFinal);

        // Ensure continuity to avoid flipping
        if (prevQ && Cesium.Quaternion.dot(prevQ, qFinal) < 0) {
            Cesium.Quaternion.negate(qFinal, qFinal);
        }

        result.push(t, qFinal.x, qFinal.y, qFinal.z, qFinal.w);
        prevQ = Cesium.Quaternion.clone(qFinal);
    }

    return result;
}

export const createPredictionSatellitePacket = (predictionSatelliteSelectedGroup, modelUrl, nadir, orbit) => {
    const quaternionData = predictionSatelliteSelectedGroup?.data?.quaternionData;
    const defaultModelUrl = getDefaultModelUrl(predictionSatelliteSelectedGroup?.data?.busSizeInUnits)
    const modelScale = getModelScale(predictionSatelliteSelectedGroup?.data?.busSizeInUnits)
    const correctedQuaternions =
        nadir && orbit && quaternionData
            ? applyInitialAxisOffset(quaternionData, nadir, orbit)
            : quaternionData;
    let satellitePacket = {
        id: "pred" + predictionSatelliteSelectedGroup?.satelliteId, // adding 'pred' prefix to avoid id conflict with existing satellite packets
        name: "pred" + predictionSatelliteSelectedGroup?.satelliteId,
        availability: predictionSatelliteSelectedGroup?.data?.availability, // using the availability from the prediction data
        model: {
            gltf: modelUrl || defaultModelUrl,
            ...modelScale
        },
        path: {
            show: [
                {
                    interval: predictionSatelliteSelectedGroup?.data?.availability,
                    boolean: true
                }
            ],
            width: 1,
            material: {
                solidColor: {
                    color: {
                        rgba: [0, 255, 0, 255]
                    }
                }
            },
            resolution: 120, // 120 to make the path smooth
            leadTime: [{ number: 5000 }], // lead time of 5000 seconds we showing 5000 seconds of future orbit
            trailTime: [{ number: 5000 }] // trail time of 5000 seconds we showing 5000 seconds of past orbit
        },
        position: {
            interpolationAlgorithm: "LAGRANGE", // using LAGRANGE as we are getting data in every 1 sec interval
            interpolationDegree: 5, // using 5 as we are getting data in every 1 sec interval
            referenceFrame: "FIXED", // ECEF Cartesian coordinates from the same presim run
            epoch: predictionSatelliteSelectedGroup?.data?.availability?.split('/')[0], // same epoch as orientation — ensures position & orientation are in sync
            cartesian: predictionSatelliteSelectedGroup?.data?.postionData // ECEF positions from presim — matches the quaternion data's time range
        },
        orientation: {
            interpolationAlgorithm: "LINEAR", // using LINEAR as we are getting data in every 1 sec interval
            interpolationDegree: 1, // using 1 as we are getting data in every 1 sec interval
            epoch: predictionSatelliteSelectedGroup?.data?.availability?.split('/')[0], // start time of the availability
            unitQuaternion: correctedQuaternions// quaternions
        }
    }

    return satellitePacket
}

export const drawAxesForSatellite = (entity: any, viewer: Cesium.Viewer) => {
    // Adjusted body-axis directions to match the coordinate system used in the 3JS canvas.
    const directions = {
        x: new Cesium.Cartesian3(0, 1, 0),
        y: new Cesium.Cartesian3(-1, 0, 0),
        z: new Cesium.Cartesian3(0, 0, 1)
    };

    const colors = {
        x: Cesium.Color.RED,
        y: Cesium.Color.GREEN,
        z: Cesium.Color.BLUE
    };

    const axisLabels = {
        x: "X",
        y: "Y",
        z: "Z"
    };

    const getScaledDirection = (dir, position) => {
        const cameraPosition = viewer.camera.positionWC;
        const distance = Cesium.Cartesian3.distance(cameraPosition, position);
        const scale = distance * 0.03; // Adjust this factor to control visual size
        return Cesium.Cartesian3.multiplyByScalar(dir, scale, new Cesium.Cartesian3());
    };

    Object.entries(directions).forEach(([axis, unitDir]) => {
        // Axis line
        viewer.entities.add({
            name: `${entity.id} ${axis.toUpperCase()} Axis`,
            polyline: {
                positions: new Cesium.CallbackProperty(function (time) {
                    const position = entity.position?.getValue(time);
                    const orientation = entity.orientation?.getValue(time);
                    if (!position || !orientation) return [];

                    const rotationMatrix = Cesium.Matrix3.fromQuaternion(orientation);
                    const rotatedDir = Cesium.Matrix3.multiplyByVector(rotationMatrix, unitDir, new Cesium.Cartesian3());
                    const scaledDir = getScaledDirection(rotatedDir, position);
                    const end = Cesium.Cartesian3.add(position, scaledDir, new Cesium.Cartesian3());

                    return [position, end];
                }, false),
                material: colors[axis],
                width: 1
            }
        });

        const positionCallback: Cesium.CallbackProperty = new Cesium.CallbackProperty(
            (time: Cesium.JulianDate) => {
                const position = entity.position?.getValue(time);
                const orientation = entity.orientation?.getValue(time);
                if (!position || !orientation) return Cesium.Cartesian3.ZERO;

                const rotationMatrix = Cesium.Matrix3.fromQuaternion(orientation);
                const rotatedDir = Cesium.Matrix3.multiplyByVector(rotationMatrix, unitDir, new Cesium.Cartesian3());
                const scaledDir = getScaledDirection(rotatedDir, position);

                return Cesium.Cartesian3.add(position, scaledDir, new Cesium.Cartesian3());
            },
            false
        );
        // Axis label
        viewer.entities.add({
            name: `${entity.id} ${axis.toUpperCase()} Label`,
            position: positionCallback as unknown as Cesium.PositionProperty,
            label: {
                text: axisLabels[axis],
                font: "12px sans-serif",
                fillColor: colors[axis],
                outlineColor: Cesium.Color.BLACK,
                outlineWidth: 2,
                style: Cesium.LabelStyle.FILL_AND_OUTLINE,
                verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                pixelOffset: new Cesium.Cartesian2(0, -20)
            }
        });
    });
};

export const hideShowAxisAndLabels = (viewer: Cesium.Viewer, show: boolean) => {
    viewer.entities.values.forEach(entity => {
        const name = entity.name || "";
        if (name.includes("Axis") || name.includes("Label")) {
            entity.show = show;
        }
    });
    requestRender(viewer);
}

export const setPredictionVisualsVisibility = (viewer: Cesium.Viewer | null, show: boolean) => {
    if (!viewer || viewer.isDestroyed()) return

    if (!show && fitToScreenTimer) {
        fitToScreenRequestId += 1;
        window.clearTimeout(fitToScreenTimer);
        fitToScreenTimer = undefined;
    }

    showHideDataSourceByName(viewer, "PREDICTION_ORBIT", show);
    NON_PREDICTION_ORBIT_DATASOURCES.forEach((dataSourceName) => {
        showHideDataSourceByName(viewer, dataSourceName, !show);
    });
    hideShowAxisAndLabels(viewer, show);

    predictionVisibilityRefs.forEach((setVisibility) => {
        try {
            setVisibility(show);
        } catch (error) {
            console.error(error);
        }
    });

    requestRender(viewer);
}

/**
 * Compute a rotation matrix for the sensor so that its +Z axis
 * points along the satellite's body-frame nadir axis (camera
 * look direction). The CesiumSensorVolumes library emits the
 * cone along the **positive Z-axis**, so +Z must be the camera
 * direction for the cone to point where the camera looks.
 *
 * @param nadirAxis - The nadir axis name from the backend (e.g. "-X", "Z").
 *   Uses getLocalAxis() to map it to the 3JS body-frame direction.
 *   Falls back to geometric nadir (-normalize(position)) when not provided.
 */
function computeNadirPointingSensorMatrix(
    position: Cesium.Cartesian3,
    orientation: Cesium.Quaternion,
    nadirAxis?: string
): Cesium.Matrix3 {
    const bodyRotation = Cesium.Matrix3.fromQuaternion(orientation, new Cesium.Matrix3());

    // Derive sensor look direction from the configured nadir axis.
    // The camera sits on this axis, so the sensor cone should point
    // along getLocalAxis(nadirAxis) transformed into world space.
    let nadir: Cesium.Cartesian3;
    if (nadirAxis) {
        nadir = Cesium.Matrix3.multiplyByVector(
            bodyRotation,
            getLocalAxis(nadirAxis),
            new Cesium.Cartesian3()
        );
        Cesium.Cartesian3.normalize(nadir, nadir);
    } else {
        // Fallback: geometric nadir (toward Earth centre)
        nadir = Cesium.Cartesian3.negate(
            Cesium.Cartesian3.normalize(position, new Cesium.Cartesian3()),
            new Cesium.Cartesian3()
        );
    }

    // Use the satellite body-frame X axis projected onto the plane
    // perpendicular to nadir so the footprint rectangle stays
    // aligned with the spacecraft.
    const bodyX = Cesium.Matrix3.multiplyByVector(
        bodyRotation,
        new Cesium.Cartesian3(0, 1, 0), // visual X axis in 3JS canvas coordinates
        new Cesium.Cartesian3()
    );

    // Remove component along nadir
    const dot = Cesium.Cartesian3.dot(bodyX, nadir);
    const proj = Cesium.Cartesian3.multiplyByScalar(nadir, dot, new Cesium.Cartesian3());
    let xAxis = Cesium.Cartesian3.subtract(bodyX, proj, new Cesium.Cartesian3());

    // Fall back to body-Y if body-X is nearly parallel to nadir
    if (Cesium.Cartesian3.magnitudeSquared(xAxis) < 1e-10) {
        const bodyY = Cesium.Matrix3.multiplyByVector(
            bodyRotation,
            new Cesium.Cartesian3(-1, 0, 0), // axis according to 3JS canvas
            new Cesium.Cartesian3()
        );
        const dot2 = Cesium.Cartesian3.dot(bodyY, nadir);
        const proj2 = Cesium.Cartesian3.multiplyByScalar(nadir, dot2, new Cesium.Cartesian3());
        xAxis = Cesium.Cartesian3.subtract(bodyY, proj2, new Cesium.Cartesian3());
    }
    Cesium.Cartesian3.normalize(xAxis, xAxis);

    // Y = Z × X  (right-hand rule: Z is nadir, X is along-track-ish)
    const yAxis = Cesium.Cartesian3.cross(nadir, xAxis, new Cesium.Cartesian3());
    Cesium.Cartesian3.normalize(yAxis, yAxis);

    // Re-orthogonalize X = Y × Z
    Cesium.Cartesian3.cross(yAxis, nadir, xAxis);
    Cesium.Cartesian3.normalize(xAxis, xAxis);

    // Build column-major rotation matrix  [X | Y | Z]
    // Z = nadir axis  →  sensor +Z points along camera look direction
    const m = new Cesium.Matrix3();
    Cesium.Matrix3.setColumn(m, 0, xAxis, m);
    Cesium.Matrix3.setColumn(m, 1, yAxis, m);
    Cesium.Matrix3.setColumn(m, 2, nadir, m);
    return m;
}

export const createCircularSensors = (
    viewer, // Cesium.Viewer
    positionProperty, // Cesium.PositionProperty
    orientationProperty, // Cesium.OrientationProperty
    timeWindows: any[] = [], // array of {start: string, end: string}
    nadirAxis?: string // nadir axis name from backend (e.g. "-X", "Z")
) => {
    if (circularSensorCleanup) {
        circularSensorCleanup();
        circularSensorCleanup = null;
    }
    if (timeWindows?.length === 0) return

    const ellipsoid = Cesium.Ellipsoid.WGS84;
    const swath = 140_000; // 140 km ground swath

    // Compute proper geodetic altitude for half-angle calculation
    const initPos = positionProperty.getValue(viewer.clock.currentTime);
    const initCarto = initPos ? ellipsoid.cartesianToCartographic(initPos) : null;
    const initAlt = initCarto ? initCarto.height : 500_000; // fallback 500 km

    // Half angle = atan( (swath/2) / altitude )
    const halfAngle = Math.atan((swath / 2) / initAlt);

    // Radius = distance from satellite to Earth center (guarantees Earth intersection)
    const initRadius = initPos ? Cesium.Cartesian3.magnitude(initPos) : initAlt + 6_371_000;

    // Create the sensor – radius reaches through Earth's surface
    const customSensor = new CesiumSensorVolumes.CustomSensorVolume({
        radius: initRadius,
        lateralSurfaceMaterial: Cesium.Material.fromType("Color", {
            color: Cesium.Color.GREEN.withAlpha(0.3),
        }),
        intersectionColor: Cesium.Color.WHITE.withAlpha(0.7),
        intersectionWidth: 2.0,
        slicePartitions: 64, // smoother edges
        stackPartitions: 32, // smoother surface
    });

    // Generate circular directions for 14km swath
    const steps = 36;
    const directions: any[] = [];

    for (let i = 0; i < steps; i++) {
        const azimuth = (i / steps) * Cesium.Math.TWO_PI;
        // Use the calculated halfAngle based on 14km swath
        directions.push(new Cesium.Spherical(azimuth, halfAngle));
    }

    customSensor.directions = directions; // set directions
    viewer.scene.primitives.add(customSensor); // add to scene
    let visualsVisible = true;

    // Update position + orientation dynamically
    const preRenderCallback = () => {
        const time = viewer.clock.currentTime;
        const position = positionProperty.getValue(time);
        if (!position) return;

        const orientation = orientationProperty.getValue(time);
        if (!orientation) return;

        // Dynamically update radius to full distance to Earth center
        customSensor.radius = Cesium.Cartesian3.magnitude(position);

        // Build a rotation so the sensor cone points along the configured nadir axis.
        const sensorRotation = computeNadirPointingSensorMatrix(position, orientation, nadirAxis);
        const finalMatrix = Cesium.Matrix4.fromRotationTranslation(sensorRotation, position, new Cesium.Matrix4());
        customSensor.modelMatrix = finalMatrix;

        // Check if current time is within any active window
        const currentTime = Cesium.JulianDate.toDate(time).getTime();
        let isVisible = false;

        for (const window of timeWindows) {
            const start = new Date(window.start).getTime();
            const end = new Date(window.end).getTime();
            if (currentTime >= start && currentTime <= end) {
                isVisible = true;
                break;
            }
        }

        customSensor.show = visualsVisible && isVisible; // enable/disable sensor visibility
    };
    viewer.scene.preRender.addEventListener(preRenderCallback);

    let cleanedUp = false;

    const setVisibility = (show: boolean) => {
        visualsVisible = show;
        customSensor.show = show && customSensor.show;
    }

    // Cleanup function
    const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        try {
            viewer.scene.preRender.removeEventListener(preRenderCallback);
        } catch (e) {
            //we can ignore
        }
        customSensor.show = false;
        if (viewer.isDestroyed()) return;
        try {
            if (viewer.scene.primitives.contains(customSensor)) {
                viewer.scene.primitives.remove(customSensor);
            }
        } catch (e) {
            // we can ignore
        }
    };

    return { sensor: customSensor, cleanup, setVisibility };
};

export const createRectangularSensorWithFootprintPath = (
    viewer: Cesium.Viewer,
    positionProperty,
    orientationProperty,
    swath = 140000,
    timeWindows: any[] = [],
    nadirAxis: string = "Z" // nadir axis name from backend (e.g. "-X", "Z")
) => {
    if (rectangularSensorCleanup) {
        rectangularSensorCleanup();
        rectangularSensorCleanup = null;
    }
    if (timeWindows?.length === 0) return

    const ellipsoid = Cesium.Ellipsoid.WGS84;

    // Compute proper geodetic altitude for half-angle calculation
    const initPos = positionProperty.getValue(viewer.clock.currentTime);
    const initCarto = initPos ? ellipsoid.cartesianToCartographic(initPos) : null;
    const initAlt = initCarto ? initCarto.height : 500_000; // fallback 500 km

    // Half angle = atan( (swath/2) / altitude )
    const halfAngle = Math.atan((swath / 2) / initAlt);

    // Radius = distance from satellite to Earth center (guarantees Earth intersection)
    const initRadius = initPos ? Cesium.Cartesian3.magnitude(initPos) : initAlt + 6_371_000;

    // Create the sensor – radius reaches through Earth's surface
    const sensor: any = new CesiumSensorVolumes.RectangularPyramidSensorVolume({
        xHalfAngle: halfAngle,
        yHalfAngle: halfAngle,
        radius: initRadius,
        lateralSurfaceMaterial: Cesium.Material.fromType("Color", {
            color: Cesium.Color.ORANGE.withAlpha(0.3),
        }),
        intersectionColor: Cesium.Color.WHITE.withAlpha(0.7),
        intersectionWidth: 2.0,
    });
    viewer.scene.primitives.add(sensor);

    // Use PolylineCollection for better performance
    const footprintCollection = new Cesium.PolylineCollection();
    viewer.scene.primitives.add(footprintCollection);
    let visualsVisible = true;

    let lastFootprintPosition: any = null;
    const MIN_DISTANCE = swath / 4;
    const footprintTrail: any[] = []; // Store references to polylines
    // Track timestamps for each footprint so we can remove future ones on rewind
    const footprintTimestamps: number[] = [];
    let previousTimeMs: number | null = null;

    const preRenderCallback = () => {
        const time = viewer.clock.currentTime;
        const position = positionProperty.getValue(time);
        const orientation = orientationProperty.getValue(time);
        if (!position || !orientation) return;

        // Dynamically update radius to full distance to Earth center
        sensor.radius = Cesium.Cartesian3.magnitude(position);

        // Build a rotation so the sensor cone points along the configured nadir axis.
        const sensorRotation = computeNadirPointingSensorMatrix(position, orientation, nadirAxis);
        sensor.modelMatrix = Cesium.Matrix4.fromRotationTranslation(sensorRotation, position);

        // Determine current active time window
        const currentTimeMs = Cesium.JulianDate.toDate(time).getTime();
        const currentWindow = timeWindows.find((window: any) => {
            const start = new Date(window.start).getTime();
            const end = new Date(window.end).getTime();
            return currentTimeMs >= start && currentTimeMs <= end;
        });

        sensor.show = visualsVisible && !!currentWindow;

        // Detect backward movement (rewind) and remove footprints in the future
        if (previousTimeMs !== null && currentTimeMs < previousTimeMs) {
            // Remove all footprints that were plotted at times beyond the current time
            while (footprintTimestamps.length > 0 &&
                footprintTimestamps[footprintTimestamps.length - 1] > currentTimeMs) {
                footprintTimestamps.pop();
                const polyline = footprintTrail.pop();
                if (polyline) {
                    footprintCollection.remove(polyline);
                }
            }
            // Reset last position so next forward step re-checks distance
            lastFootprintPosition = null;
        }
        previousTimeMs = currentTimeMs;

        if (!currentWindow) return;

        // Only add footprint if far enough from last footprint
        if (!lastFootprintPosition ||
            Cesium.Cartesian3.distance(position, lastFootprintPosition) >= MIN_DISTANCE) {

            lastFootprintPosition = Cesium.Cartesian3.clone(position);

            // Compute ground intersection points
            const directions = [
                new Cesium.Cartesian3(-halfAngle, -halfAngle, 1),
                new Cesium.Cartesian3(halfAngle, -halfAngle, 1),
                new Cesium.Cartesian3(halfAngle, halfAngle, 1),
                new Cesium.Cartesian3(-halfAngle, halfAngle, 1),
            ];

            const corners: any[] = [];
            directions.forEach(dir => {
                const rotatedDir = Cesium.Matrix3.multiplyByVector(sensorRotation, dir, new Cesium.Cartesian3());
                const normalizedDir = Cesium.Cartesian3.normalize(rotatedDir, new Cesium.Cartesian3());
                const ray = new Cesium.Ray(position, normalizedDir);
                const intersection = viewer.scene.globe.pick(ray, viewer.scene);
                if (intersection) corners.push(intersection);
            });

            if (corners.length === 4) {
                const footprintPositions = [...corners, corners[0]];
                const footprintPolyline = footprintCollection.add({
                    positions: footprintPositions,
                    width: 1,
                    material: Cesium.Material.fromType('Color', {
                        color: Cesium.Color.CYAN.withAlpha(0.7)
                    })
                });
                footprintTrail.push(footprintPolyline);
                footprintTimestamps.push(currentTimeMs);
            }
        }
    };

    viewer.scene.preRender.addEventListener(preRenderCallback);

    let cleanedUp = false;

    const setVisibility = (show: boolean) => {
        visualsVisible = show;
        sensor.show = show && sensor.show;
        footprintCollection.show = show;
    }

    // Cleanup function
    const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        try {
            viewer.scene.preRender.removeEventListener(preRenderCallback);
        } catch (e) {
            // ignore
        }
        sensor.show = false;
        footprintCollection.show = false;
        if (viewer.isDestroyed()) return;
        try {
            if (viewer.scene.primitives.contains(footprintCollection)) {
                viewer.scene.primitives.remove(footprintCollection);
            }
        } catch (e) {
            // ignore
        }
        try {
            if (viewer.scene.primitives.contains(sensor)) {
                viewer.scene.primitives.remove(sensor);
            }
        } catch (e) {
            // ignore
        }
        footprintTrail.length = 0;
        footprintTimestamps.length = 0;
    };

    return { sensor, footprintTrail, cleanup, setVisibility };
};

export const handleFitToscreenMode = (viewer: Cesium.Viewer | null) => {
    if (viewer && !viewer.isDestroyed()) {
        fitToScreenRequestId += 1;
        const requestId = fitToScreenRequestId;

        if (fitToScreenTimer) {
            window.clearTimeout(fitToScreenTimer);
        }

        fitToScreenTimer = window.setTimeout(() => {
            try {
                if (viewer.isDestroyed() || requestId !== fitToScreenRequestId || (window as any).predictionCleanupInProgress) return;

                viewer.camera.cancelFlight();
                viewer.trackedEntity = undefined;
                viewer.camera.setView({
                    destination: Cesium.Cartesian3.fromDegrees(0, 0, 32000000),
                    orientation: {
                        heading: 0,
                        pitch: -Math.PI / 2,
                        roll: 0
                    }
                });
                requestRender(viewer);
            } catch (e) {
                console.error('Error setting camera view:', e);
            }
        }, 500)
    }
}

// Function to take screenshot and download it
const takeScreenshot = (viewer: Cesium.Viewer | null) => {
    if (!viewer) return;

    viewer.render(); // Ensure latest frame is rendered
    const canvas = viewer.canvas;
    const image = canvas?.toDataURL("image/png");

    if (image) {
        // Create a blob from the image data
        const link = document.createElement('a');
        link.href = image;

        // Generate filename with timestamp
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0] + '_' + new Date().getTime();
        link.download = `screenshot_${timestamp}.png`;

        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } else {
        console.error('Failed to capture screenshot');
    }
};

export const zoomIntoTheEntity = (viewer: Cesium.Viewer | null, dataSourceName: string, entityId: string) => {
    if (!viewer || !entityId) return
    var entity = getEntityById(viewer, dataSourceName, entityId);
    if (entity) {
        viewer.flyTo(entity, {
            duration: 2,          // Duration of the flight in seconds
            offset: {             // Offset from the entity
                heading: Cesium.Math.toRadians(0),  // Camera heading in radians
                pitch: Cesium.Math.toRadians(-20),  // Camera pitch in radians
                range: 13000000       // Distance from entity in meters
            }
        }).then(() => {
            // takeScreenshot(viewer);
        });
    } else {
        console.log('Entity not found');
    }
}

function computeLVLHMatrix(
    position: Cesium.Cartesian3,
    velocity: Cesium.Cartesian3
): Cesium.Matrix3 {

    // Z → nadir (toward Earth)
    const z = Cesium.Cartesian3.normalize(
        Cesium.Cartesian3.negate(position, new Cesium.Cartesian3()),
        new Cesium.Cartesian3()
    );

    // X → along-track (velocity)
    const x = Cesium.Cartesian3.normalize(
        velocity,
        new Cesium.Cartesian3()
    );

    // Y → cross-track (completes RH system)
    const y = Cesium.Cartesian3.normalize(
        Cesium.Cartesian3.cross(z, x, new Cesium.Cartesian3()),
        new Cesium.Cartesian3()
    );

    // Re-orthogonalize X
    Cesium.Cartesian3.cross(y, z, x);
    Cesium.Cartesian3.normalize(x, x);

    const m = new Cesium.Matrix3();
    Cesium.Matrix3.setColumn(m, 0, x, m);
    Cesium.Matrix3.setColumn(m, 1, y, m);
    Cesium.Matrix3.setColumn(m, 2, z, m);

    return m;
}

function getVelocityFromPosition(
    entity: Cesium.Entity,
    time: Cesium.JulianDate
): Cesium.Cartesian3 | undefined {

    if (!entity.position) return undefined;

    const dt = 0.01;

    const p1 = entity.position.getValue(time);
    if (!p1) return undefined;

    const t2 = Cesium.JulianDate.addSeconds(time, dt, new Cesium.JulianDate());
    const p2 = entity.position.getValue(t2);
    if (!p2) return undefined;

    const v = Cesium.Cartesian3.subtract(p2, p1, new Cesium.Cartesian3());
    return Cesium.Cartesian3.normalize(v, v);
}

let yawOffset = 0;
let pitchOffset = 0;
let rollOffset = 0;
let initialized = false;

export const createPredictionOrbitPacket = async (
    viewer: Cesium.Viewer | null,
    scenarioId: any,
    missionId: any,
    predictionSatelliteSelectedGroup: any,
    multiplier: number = 2
) => {
    if (!viewer) return;
    const predictionAvailability = predictionSatelliteSelectedGroup?.data?.availability;
    const predictionStartTime = typeof predictionAvailability === 'string' ? predictionAvailability.split('/')[0] : undefined;
    const existingDataSource: any = getDataSourceByName(viewer, "PREDICTION_ORBIT");

    if (
        existingDataSource &&
        existingDataSource.__predictionSatelliteId === predictionSatelliteSelectedGroup?.satelliteId &&
        existingDataSource.__predictionAvailability === predictionAvailability
    ) {
        setPredictionVisualsVisibility(viewer, true);
        if (predictionStartTime) {
            try {
                viewer.clock.currentTime = Cesium.JulianDate.fromIso8601(predictionStartTime);
            } catch (error) {
                console.error('Error setting prediction clock start time:', error);
            }
        }
        applyPredictionClock(viewer, predictionAvailability, predictionStartTime, multiplier, existingDataSource);

        requestRender(viewer);
        return;
    }

    const satellites = await fetchOrbitData(scenarioId, missionId);

    const selectedSatellite =
        satellites?.satellites
            ?.find(s => s?.satelliteGroupId === predictionSatelliteSelectedGroup?.satelliteGroupId)
            ?.satelliteGroups
            ?.find(g => g?.satelliteId === predictionSatelliteSelectedGroup?.satelliteId) || null;

    let gsPacketList: any[] = []

    const modelInfo = await getSatelliteModelUrl(
        predictionSatelliteSelectedGroup?.satelliteGroupId,
        missionId
    );
    const modelUrl = modelInfo?.modelUrl;

    const defaultAxis = getDefaultNadirOrbitAxis(predictionSatelliteSelectedGroup?.data?.busSizeInUnits);
    const nadirAxis = modelInfo?.nadirAxis || defaultAxis.nadirAxis;
    const orbitAxis = modelInfo?.orbitAxis || defaultAxis.orbitAxis;

    selectedSatellite?.visualizationData?.satToGsConnection?.forEach((connection: any) => {
        const groundStation = getGroundStationDetailsById(
            selectedSatellite?.visualizationData?.groundStation,
            connection
        );
        gsPacketList.push(groundStation)
    });

    // Derive satellite altitude (meters) for dynamic FOR size
    let satAltitudeMeters = 500000;
    try {
        const altKmFromViz = selectedSatellite?.visualizationData?.satellites?.[0]?.satOrbitParameters?.altitude;
        if (typeof altKmFromViz === 'number' && altKmFromViz > 0) {
            satAltitudeMeters = altKmFromViz * 1000;
        } else if (typeof selectedSatellite?.satOrbitParameters?.altitude === 'number' && selectedSatellite?.satOrbitParameters?.altitude > 0) {
            satAltitudeMeters = selectedSatellite.satOrbitParameters.altitude * 1000;
        }
    } catch (e) {
        // keep fallback if structure differs
    }

    let groundStationPacket = createGroundStationAvailabilityPacket(gsPacketList, satAltitudeMeters);
    let groundStationToSatelliteConnections = groundStationToSatelliteConnection(selectedSatellite?.visualizationData?.satToGsConnection);
    let predictionSatellitePacket = createPredictionSatellitePacket(predictionSatelliteSelectedGroup, modelUrl, nadirAxis, orbitAxis)

    initialized = false;

    removePredictionDataSource(viewer, 'PREDICTION_ORBIT');
    NON_PREDICTION_ORBIT_DATASOURCES.forEach((dataSourceName) => {
        showHideDataSourceByName(viewer, dataSourceName, false);
    });
    let dataSource: any = getDataSourceByName(viewer, "PREDICTION_ORBIT");
    if (!dataSource) {
        dataSource = new Cesium.CzmlDataSource("PREDICTION_ORBIT");
        await viewer.dataSources.add(dataSource);
    }

    await dataSource.load([
        {
            clock: {
                interval: predictionAvailability, // using the availability of the prediction satellite to set the clock interval
                currentTime: predictionStartTime,
                multiplier: multiplier, // setting multiplier to 0 to pause the time
                range: "CLAMPED", // clamping the time to the availability interval
                step: "SYSTEM_CLOCK_MULTIPLIER" // using system clock multiplier to control the time
            },
            id: "document", // required id for the czml data source
            name: "PREDICTION_ORBIT", // name of the data source
            version: "1.0" // version of the czml data source
        },
        predictionSatellitePacket,
        ...groundStationPacket,
        ...groundStationToSatelliteConnections
    ]);
    dataSource.show = true;
    dataSource.__predictionSatelliteId = predictionSatelliteSelectedGroup?.satelliteId;
    dataSource.__predictionAvailability = predictionAvailability;

    if (predictionStartTime) {
        try {
            applyPredictionClock(viewer, predictionAvailability, predictionStartTime, multiplier, dataSource);
            await waitForClockSettle();
            applyPredictionClock(viewer, predictionAvailability, predictionStartTime, multiplier, dataSource);
        } catch (error) {
            console.error('Error setting prediction clock start time:', error);
        }
    } else {
        applyPredictionClock(viewer, predictionAvailability, predictionStartTime, multiplier, dataSource);
        await waitForClockSettle();
        applyPredictionClock(viewer, predictionAvailability, predictionStartTime, multiplier, dataSource);
    }

    const entity: any = dataSource.entities.getById("pred" + predictionSatelliteSelectedGroup?.satelliteId);
    if (entity) {
        entity.label = new Cesium.LabelGraphics({
            text: new Cesium.CallbackProperty(() => {
                const currentTime = viewer.clock.currentTime;
                const position = entity.position?.getValue(currentTime);
                const orientation = entity.orientation?.getValue(currentTime);

                if (!position || !orientation) return "";
                const cartographic = Cesium.Ellipsoid.WGS84.cartesianToCartographic(position);

                const lat = Cesium.Math.toDegrees(cartographic?.latitude || 0).toFixed(2);
                const lon = Cesium.Math.toDegrees(cartographic?.longitude || 0).toFixed(2);
                const alt = cartographic?.height ? cartographic?.height.toFixed(2) : 0;

                let velocity = getVelocityFromPosition(entity, currentTime);
                if (!velocity) return "";

                // ---- LVLH frame ----
                const lvlhMatrix = computeLVLHMatrix(position, velocity);

                // ---- body frame ----
                const bodyMatrix = Cesium.Matrix3.fromQuaternion(orientation);

                // ---- body relative to LVLH ----
                const localMatrix = Cesium.Matrix3.multiply(
                    Cesium.Matrix3.transpose(lvlhMatrix, new Cesium.Matrix3()),
                    bodyMatrix,
                    new Cesium.Matrix3()
                );

                const m = localMatrix;

                // helper: element(row, col)
                const e = (r: number, c: number) => m[c * 3 + r];

                // ---- BODY X AXIS expressed in LVLH ----
                const bx = new Cesium.Cartesian3(
                    e(0, 0),
                    e(1, 0),
                    e(2, 0)
                );

                // project onto LVLH XY plane
                bx.z = 0;
                Cesium.Cartesian3.normalize(bx, bx);

                // ---- TRUE LVLH YAW ----
                let heading = Math.atan2(bx.y, bx.x);

                // ---- PITCH ----
                let pitch = Math.asin(-e(2, 0));

                // ---- ROLL ----
                let roll = Math.atan2(
                    e(2, 1),
                    e(2, 2)
                );

                // degrees
                heading = Cesium.Math.toDegrees(heading);
                pitch = Cesium.Math.toDegrees(pitch);
                roll = Cesium.Math.toDegrees(roll);


                if (!initialized) {
                    yawOffset = heading;
                    pitchOffset = pitch;
                    rollOffset = roll;
                    initialized = true;
                }

                // Apply offsets to zero first frame
                heading -= yawOffset;
                pitch -= pitchOffset;
                roll -= rollOffset;

                emitSatelliteUpdate({
                    id: predictionSatelliteSelectedGroup?.satelliteId,
                    lat: Number(lat),
                    lon: Number(lon),
                    alt: Number(alt) / 1000,
                    heading: Number(heading),
                    pitch: Number(pitch),
                    roll: Number(roll)
                });
            }, false),
        });

        drawAxesForSatellite(entity, viewer)

        let earthObservationTimeInterval: any[] = []
        let communicationTimeInterval: any[] = []

        predictionSatelliteSelectedGroup?.data?.events?.map((interval) => {
            switch (interval.payloadType) {
                case "Earth Observation":
                    earthObservationTimeInterval.push(interval)
                    break;
                case "Communication":
                    communicationTimeInterval.push(interval)
                    break;
                default:
                    earthObservationTimeInterval.push(interval)

            }
        })
        const earthObs = createRectangularSensorWithFootprintPath(viewer, entity?.position, entity?.orientation, 14000, earthObservationTimeInterval, nadirAxis);
        const commObs = createCircularSensors(viewer, entity?.position, entity?.orientation, communicationTimeInterval, nadirAxis)
        if (earthObs?.cleanup) predictionCleanupRefs.push(earthObs.cleanup);
        if (commObs?.cleanup) predictionCleanupRefs.push(commObs.cleanup);
        if (earthObs?.setVisibility) predictionVisibilityRefs.push(earthObs.setVisibility);
        if (commObs?.setVisibility) predictionVisibilityRefs.push(commObs.setVisibility);
    }
    requestRender(viewer);
}

export const highlightGroupSatellite = (viewer: Cesium.Viewer | null, satellitesColor: { [satId: string]: string }, satelliteId: string) => {
    if (!viewer) return
    try {
        const datasource: any = getDataSourceByName(viewer, 'ORBIT_OBJECT')
        if (datasource && datasource.entities && datasource.entities.values) {
            for (const entity of datasource.entities.values) {
                if (entity.id === satelliteId && Cesium.defined(entity.billboard) && Cesium.defined(entity.path)) {
                    entity.path.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromBytes(0, 245, 0, 240)); // orbit highlight color
                    entity.billboard.image = '/assets/satellite_default.svg';
                } else if (Cesium.defined(entity.billboard) && Cesium.defined(entity.path)) {
                    const rgbaArray = satellitesColor[entity.id] ? JSON.parse(satellitesColor[entity.id]) : DEFAULT_ORBIT_COLOR;
                    rgbaArray[3] = 150;
                    entity.path.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromBytes(...rgbaArray)); // orbit highlight color
                    entity.billboard.image = '/assets/satellite_default.svg';
                }
                requestRender(viewer);
            }
        }
    } catch (error) {
        console.log(error);
    }
}


export function removePredictionDataSource(viewer: Cesium.Viewer, dataSourceName: string | "PREDICTION_ORBIT") {
    if (!viewer || viewer.isDestroyed()) return;

    try {
        // Clean up local prediction cleanup refs (sensors, footprints, etc.)
        if (predictionCleanupRefs?.length) {
            predictionCleanupRefs.forEach(fn => {
                try {
                    fn();
                } catch (e) {
                    console.error(e);
                }
            });
            predictionCleanupRefs = [];
        }
        predictionVisibilityRefs = [];
        // Clean up window-level prediction cleanup refs
        if ((window as any).predictionCleanupRefs?.length) {
            (window as any).predictionCleanupRefs.forEach(fn => {
                try {
                    fn();
                } catch (e) {
                    console.error(e);
                }
            });
            (window as any).predictionCleanupRefs = [];
        }
        // Clean up circular and rectangular sensor footprints
        // Clean up circular and rectangular sensor footprints
        circularSensorCleanup?.();
        rectangularSensorCleanup?.();

        circularSensorCleanup = null;
        rectangularSensorCleanup = null;

        viewer.entities.values
            .filter(e => e.name?.includes("Axis") || e.name?.includes("Label"))
            .forEach(e => viewer.entities.remove(e));

        // Remove PREDICTION_ORBIT fully so Cesium re-adds it fresh on the next play.
        // This ensures viewer.clockTrackedDataSource is set correctly on each play —
        // Cesium only tracks a data source when it is ADDED, not when re-load()ed.
        removeDataSourceByName(viewer, dataSourceName);
        showHideDataSourceByName(viewer, "ORBIT_OBJECT", true);
        requestRender(viewer);

    } catch (err) {
        console.error("Error while removing datasource:", err);
    }
}


/**
 * Clear all data sources and entities from the viewer
 * @param viewer - Cesium viewer instance
 * @param options - Optional configuration for selective clearing
 * @param options.keepDataSources - Array of data source names to preserve
 * @param options.clearEntities - Whether to clear entities (default: true)
 * @param options.clearDataSources - Whether to clear data sources (default: true)
 */
export const clearViewer = (
    viewer: Cesium.Viewer | null,
    options?: {
        keepDataSources?: string[],
        clearEntities?: boolean,
        clearDataSources?: boolean
    }
) => {
    if (!viewer) return;

    const {
        keepDataSources = [],
        clearEntities = true,
        clearDataSources = true
    } = options || {};

    try {
        // Clear all data sources except those in keepDataSources list
        if (clearDataSources) {
            const dataSourcesToRemove: Cesium.DataSource[] = [];
            for (let i = 0; i < viewer.dataSources.length; i++) {
                const dataSource = viewer.dataSources.get(i);
                if (!keepDataSources.includes(dataSource.name)) {
                    dataSourcesToRemove.push(dataSource);
                }
            }
            dataSourcesToRemove.forEach(ds => viewer.dataSources.remove(ds, false));
        }

        // Clear all entities
        if (clearEntities) {
            viewer.entities.removeAll();
        }

        // Clean up sensor cleanup functions
        if (circularSensorCleanup) {
            circularSensorCleanup();
            circularSensorCleanup = null;
        }
        if (rectangularSensorCleanup) {
            rectangularSensorCleanup();
            rectangularSensorCleanup = null;
        }

        // Clean up prediction cleanup refs
        if (predictionCleanupRefs.length > 0) {
            predictionCleanupRefs.forEach(cleanup => {
                try {
                    cleanup();
                } catch (e) {
                    console.error("Error during prediction cleanup:", e);
                }
            });
            predictionCleanupRefs = [];
        }
        predictionVisibilityRefs = [];

        // Clear window prediction cleanup refs if they exist
        if ((window as any).predictionCleanupRefs) {
            (window as any).predictionCleanupRefs.forEach((cleanup: any) => {
                try {
                    cleanup();
                } catch (e) {
                    console.error("Error during window prediction cleanup:", e);
                }
            });
            (window as any).predictionCleanupRefs = [];
        }

        // Request render to update the view
        requestRender(viewer);
    } catch (error) {
        console.error("Error while clearing viewer:", error);
    }
};

/**
 * Plot satellites from scenario JSON into the viewer
 * @param viewer - Cesium viewer instance
 * @param satellites - Array of satellite elements from scenarioJson
 * @param scenarioStartTime - Scenario start time for clock configuration
 * @param scenarioDuration - Scenario duration in seconds
 */
export const plotScenariosatellites = (
    viewer: Cesium.Viewer | null,
    satellites: any[],
    scenarioStartTime: string,
    scenarioDuration: number
) => {
    if (!viewer || !satellites || satellites.length === 0) return;

    try {

        // Create data source for satellites
        const dataSource = new Cesium.CustomDataSource('SCENARIO_SATELLITES');
        viewer.dataSources.add(dataSource);
        // Configure viewer clock
        const startTime = Cesium.JulianDate.fromIso8601(scenarioStartTime);
        const stopTime = Cesium.JulianDate.addSeconds(
            startTime,
            scenarioDuration,
            new Cesium.JulianDate()
        );
        viewer.clock.startTime = startTime;
        viewer.clock.stopTime = stopTime;
        viewer.clock.currentTime = startTime;
        viewer.clock.shouldAnimate = false; // start paused
        viewer.clock.multiplier = 0;

        const colors = [
            Cesium.Color.fromCssColorString('#FF6B6B'),
            Cesium.Color.fromCssColorString('#4ECDC4'),
            Cesium.Color.fromCssColorString('#45B7D1'),
            Cesium.Color.fromCssColorString('#FFA07A'),
        ];

        satellites.forEach((satellite, index) => {
            const satelliteName = satellite?.element_name || `Satellite_${index}`;
            const orbitConfig = satellite?.element_config?.orbit;

            if (!orbitConfig) return;
            const {
                altitude,
                eccentricity,
                inclination,
                RAAN,
                AP: argumentOfPeriapsis,
                TA: trueAnomaly
            } = orbitConfig;

            // Generate orbital track points
            const orbitalTrackPoints = generateOrbitalTrackFromElements(
                {
                    altitude,
                    eccentricity,
                    inclination,
                    RAAN,
                    argumentOfPeriapsis
                },
                90 // Increased from 360 for smoother orbit
            );
            // Add orbit path as polyline
            const orbitEntity = new Cesium.Entity({
                name: `${satelliteName}_ORBIT`,
                polyline: {
                    positions: new Cesium.CallbackProperty(() => orbitalTrackPoints, false),
                    width: 1,
                    material: Cesium.Color.fromCssColorString('#E6A64E'),
                    arcType: Cesium.ArcType.NONE,
                    show: true
                }
            });
            dataSource.entities.add(orbitEntity);

            // Add satellite as point/billboard
            const satelliteEntity = new Cesium.Entity({
                name: satelliteName,
                orientation: new Cesium.ConstantProperty(Cesium.Quaternion.IDENTITY),
                position: new Cesium.CallbackProperty(() => {
                    if (!orbitalTrackPoints || orbitalTrackPoints.length === 0) {
                        return orbitalTrackPoints[0];
                    }
                    // Get current position based on viewer clock
                    const currentTime = viewer.clock.currentTime;
                    const elapsedSeconds = Cesium.JulianDate.secondsDifference(currentTime, startTime);
                    const totalSeconds = scenarioDuration;
                    const progress = (elapsedSeconds % totalSeconds) / totalSeconds;
                    const pointIndex = Math.floor(progress * (orbitalTrackPoints.length - 1));
                    return orbitalTrackPoints[pointIndex];
                }, false) as unknown as Cesium.PositionProperty,
                model: {
                    uri: '/models/satellite.glb',
                    minimumPixelSize: 64,
                    maximumScale: 20000,
                    distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0.0, 4000000.0)
                },
                point: {
                    pixelSize: 8,
                    color: colors[index % colors.length],
                    outlineColor: Cesium.Color.WHITE,
                    outlineWidth: 2,
                    show: true,
                    distanceDisplayCondition: new Cesium.DistanceDisplayCondition(1500000.0, 1.0e9)
                },
                label: {
                    text: truncateName(satelliteName, 12, 6, 6),
                    font: '12px sans-serif',
                    fillColor: Cesium.Color.WHITE,
                    outlineColor: Cesium.Color.BLACK,
                    outlineWidth: 1,
                    pixelOffset: new Cesium.Cartesian2(0, -20),
                    show: true
                },
            });
            dataSource.entities.add(satelliteEntity);

        });

        requestRender(viewer);
    } catch (error) {
        console.error('Error plotting satellites:', error);
    }
};

/**
 * Plot ground stations from scenario JSON into the viewer
 * @param viewer - Cesium viewer instance
 * @param groundStations - Array of ground station elements from scenarioJson
 */
export const plotScenarioGroundStations = (
    viewer: Cesium.Viewer | null,
    groundStations: any[]
) => {
    if (!viewer || !groundStations || groundStations.length === 0) return;

    try {
        // Create data source for ground stations
        const dataSource = new Cesium.CustomDataSource('SCENARIO_GROUND_STATIONS');
        viewer.dataSources.add(dataSource);

        groundStations.forEach((gs, index) => {
            const gsName = gs?.element_name || `GroundStation_${index}`;
            const location = gs?.element_config?.location;

            if (!location) return;

            const { latitude, longitude, altitude } = location;

            // Create ground station entity
            const gsEntity = new Cesium.Entity({
                name: gsName,
                orientation: new Cesium.ConstantProperty(Cesium.Quaternion.IDENTITY),
                position: Cesium.Cartesian3.fromDegrees(longitude, latitude, altitude * 1000), // Convert km to m
                model: {
                    uri: '/models/groundstation.glb',
                    minimumPixelSize: 32,
                    maximumScale: 20000,
                    distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0.0, 2000000.0)
                },
                point: {
                    pixelSize: 10,
                    color: Cesium.Color.fromCssColorString('#FFD700'),
                    outlineColor: Cesium.Color.BLACK,
                    outlineWidth: 2,
                    show: true,
                    distanceDisplayCondition: new Cesium.DistanceDisplayCondition(800000.0, 1.0e9)
                },
                label: {
                    text: truncateName(gsName, 12, 6, 6),
                    font: '12px sans-serif',
                    fillColor: Cesium.Color.WHITE,
                    outlineColor: Cesium.Color.BLACK,
                    outlineWidth: 1,
                    pixelOffset: new Cesium.Cartesian2(0, -20),
                    show: true
                }
            });
            dataSource.entities.add(gsEntity);

        });

        requestRender(viewer);
    } catch (error) {
        console.error('Error plotting ground stations:', error);
    }
};

/**
 * Plot missile trajectories from scenario JSON into the viewer
 * @param viewer - Cesium viewer instance
 * @param missiles - Array of missile elements from scenarioJson
 * @param scenarioStartTime - Scenario start time for clock configuration
 * @param scenarioDuration - Scenario duration in seconds
 */
export const plotScenarioMissiles = (
    viewer: Cesium.Viewer | null,
    missiles: any[],
    scenarioStartTime: string,
    scenarioDuration: number
) => {
    if (!viewer || !missiles || missiles.length === 0) return;

    try {
        // Create data source for missiles
        const dataSource = new Cesium.CustomDataSource('SCENARIO_MISSILES');
        viewer.dataSources.add(dataSource);

        const startTime = Cesium.JulianDate.fromIso8601(scenarioStartTime);

        missiles.forEach((missile, index) => {
            const missileName = missile?.element_name || `Missile_${index}`;
            const trajectory = missile?.simulator_config?.generic_object?.trajectory;

            if (!trajectory) return;

            const { latitudes, longitudes, altitudes, times } = trajectory;

            // Create trajectory positions
            const trajectoryPositions: Cesium.Cartesian3[] = [];
            latitudes.forEach((lat: number, i: number) => {
                const lon = longitudes[i];
                const alt = (altitudes[i] || 0) * 1000; // Convert to meters
                trajectoryPositions.push(Cesium.Cartesian3.fromDegrees(lon, lat, alt));
            });

            // Add missile current position with red triangle billboard (same as GenericSimulator)
            const initialPosition = Cesium.Cartesian3.fromDegrees(longitudes[0], latitudes[0], 0);
            const missileEntity = new Cesium.Entity({
                name: missileName,
                position: initialPosition,
                billboard: {
                    image: getMissileTriangleImage() as any,
                    width: 12,
                    height: 12,
                    alignedAxis: Cesium.Cartesian3.ZERO,
                    verticalOrigin: Cesium.VerticalOrigin.CENTER,
                    horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
                },
                label: {
                    text: truncateName(missileName, 12, 6, 6),
                    font: '12px Open Sans',
                    fillColor: Cesium.Color.WHITE,
                    outlineColor: Cesium.Color.BLACK,
                    outlineWidth: 2,
                    style: Cesium.LabelStyle.FILL_AND_OUTLINE,
                    pixelOffset: new Cesium.Cartesian2(0, -16),
                    showBackground: true,
                    backgroundColor: Cesium.Color.BLACK.withAlpha(0.6),
                    backgroundPadding: new Cesium.Cartesian2(6, 4),
                    horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
                    verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                }
            });
            dataSource.entities.add(missileEntity);

        });

        requestRender(viewer);
    } catch (error) {
        console.error('Error plotting missiles:', error);
    }
};

/**
 * Plot generic elements (e.g., aircraft, vessels) as static points if they are not satellites/groundstations/missiles
 * @param viewer - Cesium viewer instance
 * @param elements - Array of elements from scenarioJson
 */
export const plotScenarioGenericElements = (
    viewer: Cesium.Viewer | null,
    elements: any[]
) => {
    if (!viewer || !elements || elements.length === 0) return;

    try {
        const dataSource = new Cesium.CustomDataSource('SCENARIO_GENERIC_ELEMENTS');
        viewer.dataSources.add(dataSource);

        elements.forEach((el, index) => {
            const name = el?.element_name || `Element_${index}`;
            const location = el?.element_config?.location;
            if (!location) return;

            const { latitude, longitude, altitude } = location;
            if (typeof latitude !== 'number' || typeof longitude !== 'number') return;

            const altitudeMeters = typeof altitude === 'number' ? altitude * 1000 : 0;

            const entity = new Cesium.Entity({
                name,
                orientation: new Cesium.ConstantProperty(Cesium.Quaternion.IDENTITY),
                position: Cesium.Cartesian3.fromDegrees(longitude, latitude, altitudeMeters),
                model: {
                    uri: `/models/${el?.element_config?.element_type || 'generic'}.glb`,
                    minimumPixelSize: 32,
                    maximumScale: 20000,
                    distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0.0, 20000000.0)
                },
                point: {
                    pixelSize: 10,
                    color: Cesium.Color.fromCssColorString('#4E79A7'),
                    outlineColor: Cesium.Color.WHITE,
                    outlineWidth: 2,
                    show: true,
                    distanceDisplayCondition: new Cesium.DistanceDisplayCondition(20000000.0, 1.0e9)
                },
                label: {
                    text: truncateName(name, 12, 6, 6),
                    font: '12px sans-serif',
                    fillColor: Cesium.Color.WHITE,
                    outlineColor: Cesium.Color.BLACK,
                    outlineWidth: 1,
                    pixelOffset: new Cesium.Cartesian2(0, -20),
                    show: true
                }
            });

            dataSource.entities.add(entity);

        });

        requestRender(viewer);
    } catch (error) {
        console.error('Error plotting generic elements:', error);
    }
};

/**
 * Plot aircraft from scenario JSON into the viewer
 * @param viewer - Cesium viewer instance
 * @param aircraft - Array of aircraft elements from scenarioJson
 */
export const plotScenarioAircraft = (
    viewer: Cesium.Viewer | null,
    aircraft: any[]
) => {
    if (!viewer || !aircraft || aircraft.length === 0) return;

    try {
        // Create data source for aircraft
        const dataSource = new Cesium.CustomDataSource('SCENARIO_AIRCRAFT');
        viewer.dataSources.add(dataSource);

        aircraft.forEach((ac, index) => {
            const acName = ac?.element_name || `Aircraft_${index}`;
            const location = ac?.element_config?.location;

            if (!location) return;

            const { latitude, longitude, altitude } = location;

            // Create aircraft entity
            const acEntity = new Cesium.Entity({
                name: acName,
                orientation: new Cesium.ConstantProperty(Cesium.Quaternion.IDENTITY),
                position: Cesium.Cartesian3.fromDegrees(longitude, latitude, altitude * 1000), // Convert km to m
                model: {
                    uri: '/models/aircraft.glb',
                    minimumPixelSize: 32,
                    maximumScale: 20000,
                    distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0.0, 2000000.0)
                },
                point: {
                    pixelSize: 10,
                    color: Cesium.Color.fromCssColorString('#FF6B9D'),
                    outlineColor: Cesium.Color.BLACK,
                    outlineWidth: 2,
                    show: true,
                    distanceDisplayCondition: new Cesium.DistanceDisplayCondition(800000.0, 1.0e9)
                },
                label: {
                    text: truncateName(acName, 12, 6, 6),
                    font: '12px sans-serif',
                    fillColor: Cesium.Color.WHITE,
                    outlineColor: Cesium.Color.BLACK,
                    outlineWidth: 1,
                    pixelOffset: new Cesium.Cartesian2(0, -20),
                    show: true
                }
            });
            dataSource.entities.add(acEntity);

        });

        requestRender(viewer);
    } catch (error) {
        console.error('Error plotting aircraft:', error);
    }
};

/**
 * Plot vessels from scenario JSON into the viewer
 * @param viewer - Cesium viewer instance
 * @param vessels - Array of vessel elements from scenarioJson
 */
export const plotScenarioVessels = (
    viewer: Cesium.Viewer | null,
    vessels: any[]
) => {
    if (!viewer || !vessels || vessels.length === 0) return;

    try {
        // Create data source for vessels
        const dataSource = new Cesium.CustomDataSource('SCENARIO_VESSELS');
        viewer.dataSources.add(dataSource);

        vessels.forEach((vessel, index) => {
            const vesselName = vessel?.element_name || `Vessel_${index}`;
            const location = vessel?.element_config?.location;

            if (!location) return;

            const { latitude, longitude, altitude } = location;

            // Create vessel entity
            const vesselEntity = new Cesium.Entity({
                name: vesselName,
                orientation: new Cesium.ConstantProperty(Cesium.Quaternion.IDENTITY),
                position: Cesium.Cartesian3.fromDegrees(longitude, latitude, altitude * 1000), // Convert km to m
                model: {
                    uri: '/models/vessel.glb',
                    minimumPixelSize: 32,
                    maximumScale: 20000,
                    distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0.0, 2000000.0)
                },
                point: {
                    pixelSize: 10,
                    color: Cesium.Color.fromCssColorString('#00D9FF'),
                    outlineColor: Cesium.Color.BLACK,
                    outlineWidth: 2,
                    show: true,
                    distanceDisplayCondition: new Cesium.DistanceDisplayCondition(800000.0, 1.0e9)
                },
                label: {
                    text: truncateName(vesselName, 12, 6, 6),
                    font: '12px sans-serif',
                    fillColor: Cesium.Color.WHITE,
                    outlineColor: Cesium.Color.BLACK,
                    outlineWidth: 1,
                    pixelOffset: new Cesium.Cartesian2(0, -20),
                    show: true
                }
            });
            dataSource.entities.add(vesselEntity);

        });

        requestRender(viewer);
    } catch (error) {
        console.error('Error plotting vessels:', error);
    }
};

/**
 * Plot all scenario elements into the viewer
 * @param viewer - Cesium viewer instance
 * @param scenarioJson - Complete scenario JSON object
 */
export const plotScenarioElements = (
    viewer: Cesium.Viewer | null,
    scenarioJson: any
) => {
    if (!viewer || !scenarioJson) return;
    viewer.scene.morphTo3D(0);
    try {
        const { elements, scenario_start_time, scenario_duration_seconds } = scenarioJson;

        if (!elements || elements.length === 0) return;

        // Separate elements by type
        const satellites = elements.filter(
            (el: any) => el?.element_config?.element_type === 'satellite'
        );
        const groundStations = elements.filter(
            (el: any) => el?.element_config?.element_type === 'groundstation'
        );
        const missiles = elements.filter(
            (el: any) => el?.element_config?.element_type === 'missile'
        );
        const aircraft = elements.filter(
            (el: any) => el?.element_config?.element_type === 'aircraft'
        );
        const vessels = elements.filter(
            (el: any) => el?.element_config?.element_type === 'vessel'
        );
        const genericElements = elements.filter((el: any) => {
            const type = el?.element_config?.element_type;
            return type !== 'satellite' && type !== 'groundstation' && type !== 'missile' && type !== 'aircraft' && type !== 'vessel';
        });

        // Plot each element type
        if (satellites.length > 0) {
            plotScenariosatellites(viewer, satellites, scenario_start_time, scenario_duration_seconds);
        }
        if (groundStations.length > 0) {
            plotScenarioGroundStations(viewer, groundStations);
        }
        if (missiles.length > 0) {
            plotScenarioMissiles(viewer, missiles, scenario_start_time, scenario_duration_seconds);
        }
        if (aircraft.length > 0) {
            plotScenarioAircraft(viewer, aircraft);
        }
        if (vessels.length > 0) {
            plotScenarioVessels(viewer, vessels);
        }
        if (genericElements.length > 0) {
            plotScenarioGenericElements(viewer, genericElements);
        }

        requestRender(viewer);
    } catch (error) {
        console.error('Error plotting scenario elements:', error);
    }
};
